import type { LocalInventoryFilters, FilterDisplayOptions, NumericFilterCondition } from '@/types';
import { DEFAULT_FILTER_ITEMS } from '@/types';
import { getHasActiveValuesForFilter } from './filter-column-mapping';
import { isNumericConditionActive } from './apply-numeric-condition';
import { formatMultiSelectValue } from '@/utils/filters/format-multi-select-value';

const STRIP_SKIP_IDS = new Set(['statement', 'editColumns', 'export']);

const CONDITION_KEYS: Record<string, keyof LocalInventoryFilters> = {
  quantity: 'quantityCondition',
  settledQuantity: 'settledQuantityCondition',
  quantityT1: 'quantityT1Condition',
  quantityT2: 'quantityT2Condition',
  quantityTx: 'quantityTxCondition',
  leverageRate: 'leverageRateCondition',
  valueEUR: 'valueEURCondition',
  currencyBenchmark: 'currencyBenchmarkCondition',
  dailyPotential: 'dailyPotentialCondition',
  adtv30D: 'adtv30DCondition',
  adtvMultiple: 'adtvMultipleCondition',
  adtvAdjusted: 'adtvAdjustedCondition',
  benchmarkFee: 'benchmarkFeeCondition',
  activeUtilisation: 'activeUtilisationCondition',
  quantityOnLoan: 'quantityOnLoanCondition',
  rebateRate: 'rebateRateCondition',
  pendingTransfersOut: 'pendingTransfersOutCondition',
  cimbEq2: 'cimbEq2QuantityCondition',
  cimbSf: 'cimbSfQuantityCondition',
  cimbSf3: 'cimbSf3QuantityCondition',
  velocityL: 'velocityLQuantityCondition',
  velocityM: 'velocityMQuantityCondition',
};

export interface AppliedFilterChip {
  filterId: string;
  label: string;
  /** False when the chip has no click target (e.g. its filter UI lives outside
   *  the sidebar) — the strip then renders a plain label instead of a button
   *  promising an action (LR-474 r3 #2). Defaults to clickable. */
  clickable?: boolean;
}

/**
 * Full option arrays per multi-select filter, used to detect "all selected" /
 * "all except N" cases when rendering chip values. When omitted, multi-select
 * chips fall back to a comma-joined list of selected values (legacy behavior).
 */
export interface FilterOptionSets {
  accountId?: string[];
  counterparty?: string[];
  currency?: string[];
  exchange?: string[];
  caStatus?: string[];
  brokerAccountName?: string[];
  brokerSettlementCounterparty?: string[];
  l2cpCounterparties?: string[];
}


const formatNumericConditionValue = (cond: NumericFilterCondition): string => {
  if (!cond) return '';
  const v1 = cond.value1;
  const v2 = cond.value2;
  switch (cond.operator) {
    case 'between':
      if (v1 != null && v2 != null && !Number.isNaN(v1) && !Number.isNaN(v2)) return `${v1}-${v2}`;
      if (v1 != null && !Number.isNaN(v1)) return `${v1}+`;
      if (v2 != null && !Number.isNaN(v2)) return `≤${v2}`;
      return '';
    case 'lessThan':
      return v1 != null && !Number.isNaN(v1) ? `< ${v1}` : '';
    case 'greaterThan':
      return v1 != null && !Number.isNaN(v1) ? `> ${v1}` : '';
    case 'equals':
      return v1 != null && !Number.isNaN(v1) ? `= ${v1}` : '';
    case 'notEqual':
      return v1 != null && !Number.isNaN(v1) ? `≠ ${v1}` : '';
    case 'isEmpty':
      return 'Empty';
    case 'isNotEmpty':
      return 'Not empty';
    default:
      return '';
  }
};

const L2CP_SUB_LABELS: Record<string, string> = {
  l2cpCounterparties: 'LTCP Counterparty',
  l2cpQuantity: 'LTCP Quantity',
  l2cpAmountEur: 'LTCP Amount (in EUR)',
  l2cpRate: 'LTCP Rate',
  l2cpGrossRate: 'LTCP Gross Rate',
  l2cpSnpDelta: 'LTCP SnP Delta',
};

const getFilterValueLabel = (
  filters: LocalInventoryFilters,
  filterId: string,
  optionSets?: FilterOptionSets
): string => {
  if (filterId === 'l2cpCounterparties') {
    return formatMultiSelectValue(filters.l2cpCounterparties ?? [], optionSets?.l2cpCounterparties);
  }
  if (filterId === 'l2cpQuantity' && filters.l2cpQuantityCondition) {
    return formatNumericConditionValue(filters.l2cpQuantityCondition);
  }
  if (filterId === 'l2cpAmountEur' && filters.l2cpAmountEurCondition) {
    return formatNumericConditionValue(filters.l2cpAmountEurCondition);
  }
  if (filterId === 'l2cpRate' && filters.l2cpRateCondition) {
    return formatNumericConditionValue(filters.l2cpRateCondition);
  }
  if (filterId === 'l2cpGrossRate' && filters.l2cpGrossRateCondition) {
    return formatNumericConditionValue(filters.l2cpGrossRateCondition);
  }
  if (filterId === 'l2cpSnpDelta' && filters.l2cpSnpDeltaCondition) {
    return formatNumericConditionValue(filters.l2cpSnpDeltaCondition);
  }
  const cKey = CONDITION_KEYS[filterId];
  if (cKey) {
    const cond = filters[cKey] as NumericFilterCondition;
    return cond ? formatNumericConditionValue(cond) : '';
  }
  if (filterId === 'accountId') return formatMultiSelectValue(filters.accountIds, optionSets?.accountId);
  if (filterId === 'counterparty') return formatMultiSelectValue(filters.counterparties, optionSets?.counterparty);
  if (filterId === 'currency') return formatMultiSelectValue(filters.currencies, optionSets?.currency);
  if (filterId === 'exchange') return formatMultiSelectValue(filters.exchanges, optionSets?.exchange);
  if (filterId === 'caStatus') return formatMultiSelectValue(filters.caStatuses, optionSets?.caStatus);
  if (filterId === 'brokerAccountName') return formatMultiSelectValue(filters.brokerAccountNames, optionSets?.brokerAccountName);
  if (filterId === 'brokerSettlementCounterparty') return formatMultiSelectValue(filters.brokerSettlementCounterparties, optionSets?.brokerSettlementCounterparty);
  if (filterId === 'exDate') {
    const from = filters.exDateFrom ?? '';
    const to = filters.exDateTo ?? '';
    if (from && to) return `${from} – ${to}`;
    if (from) return `from ${from}`;
    if (to) return `to ${to}`;
    return '';
  }
  return '';
};

/**
 * Returns chips for all currently active filters (for Applied Filters strip).
 * Label format: "Filter name: value" (e.g. "Quantity: 1-2").
 * Lent to Counterparties is expanded into one chip per active sub-filter (e.g. LTCP Quantity: 1-2).
 */
export const getAppliedFilterChips = (
  filters: LocalInventoryFilters,
  optionSets?: FilterOptionSets
): AppliedFilterChip[] => {
  const chips: AppliedFilterChip[] = [];
  for (const item of DEFAULT_FILTER_ITEMS) {
    if (STRIP_SKIP_IDS.has(item.id)) continue;
    if (item.id === 'l2cpGroup') {
      if ((filters.l2cpCounterparties?.length ?? 0) > 0) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpCounterparties', optionSets);
        chips.push({ filterId: 'l2cpCounterparties', label: `${L2CP_SUB_LABELS.l2cpCounterparties}: ${valueLabel}` });
      }
      if (isNumericConditionActive(filters.l2cpQuantityCondition)) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpQuantity', optionSets);
        chips.push({ filterId: 'l2cpQuantity', label: `${L2CP_SUB_LABELS.l2cpQuantity}: ${valueLabel}` });
      }
      if (isNumericConditionActive(filters.l2cpAmountEurCondition)) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpAmountEur', optionSets);
        chips.push({ filterId: 'l2cpAmountEur', label: `${L2CP_SUB_LABELS.l2cpAmountEur}: ${valueLabel}` });
      }
      if (isNumericConditionActive(filters.l2cpRateCondition)) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpRate', optionSets);
        chips.push({ filterId: 'l2cpRate', label: `${L2CP_SUB_LABELS.l2cpRate}: ${valueLabel}` });
      }
      if (isNumericConditionActive(filters.l2cpGrossRateCondition)) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpGrossRate', optionSets);
        chips.push({ filterId: 'l2cpGrossRate', label: `${L2CP_SUB_LABELS.l2cpGrossRate}: ${valueLabel}` });
      }
      if (isNumericConditionActive(filters.l2cpSnpDeltaCondition)) {
        const valueLabel = getFilterValueLabel(filters, 'l2cpSnpDelta', optionSets);
        chips.push({ filterId: 'l2cpSnpDelta', label: `${L2CP_SUB_LABELS.l2cpSnpDelta}: ${valueLabel}` });
      }
      continue;
    }
    if (!getHasActiveValuesForFilter(filters, item.id)) continue;
    const valueLabel = getFilterValueLabel(filters, item.id, optionSets);
    const label = valueLabel ? `${item.label}: ${valueLabel}` : item.label;
    chips.push({ filterId: item.id, label });
  }
  return chips;
};

const FILTER_LABEL_BY_ID: Record<string, string> = Object.fromEntries(
  DEFAULT_FILTER_ITEMS.map((item) => [item.id, item.label])
);

/**
 * Returns chips for Hide 0 / Hide blanks display options.
 * Each enabled option is a separate chip (e.g. "Quantity: Hide 0").
 */
export const getDisplayOptionChips = (
  filterDisplayOptions: FilterDisplayOptions
): AppliedFilterChip[] => {
  const chips: AppliedFilterChip[] = [];
  for (const [filterId, opts] of Object.entries(filterDisplayOptions)) {
    if (!opts) continue;
    const label = FILTER_LABEL_BY_ID[filterId] ?? L2CP_SUB_LABELS[filterId] ?? filterId;
    if (opts.hideZero) {
      chips.push({ filterId: `hideZero:${filterId}`, label: `${label}: Hide 0` });
    }
    if (opts.hideBlanks) {
      chips.push({ filterId: `hideBlanks:${filterId}`, label: `${label}: Hide blanks` });
    }
  }
  return chips;
};

/**
 * Count of individual Hide 0 / Hide blanks options enabled (each counts as +1).
 */
export const getHideZeroBlanksCount = (
  filterDisplayOptions: FilterDisplayOptions
): number => {
  let count = 0;
  for (const opts of Object.values(filterDisplayOptions)) {
    if (opts?.hideZero) count++;
    if (opts?.hideBlanks) count++;
  }
  return count;
};

/**
 * Total count for "Applied filters: N" (chips + hideZero/hideBlanks options).
 */
export const getAppliedFiltersCount = (
  filters: LocalInventoryFilters,
  filterDisplayOptions: FilterDisplayOptions,
  optionSets?: FilterOptionSets
): number => {
  return getAppliedFilterChips(filters, optionSets).length + getHideZeroBlanksCount(filterDisplayOptions);
};
